import 'assets/css/modal.css';
import 'styles/custom.css';
import * as React from 'react';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { Pivot, PivotItem } from 'office-ui-fabric-react/lib/Pivot';
import { Dialog, DialogType, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import {
    isAdmin,
    GetDefaultBatchSize,
    SetDefaultBatchSize,
    DeleteDefaultBatchSize,
    SetTenantBatchSize,
    DeleteTenantBatchSize,
    SetSiteBatchSize,
    DeleteSiteBatchSize,
    GetBatchSizeOverrides,
    ResolveEffectiveBatchSize,
    RefreshBatchSizeConfiguration
} from "../components/Requests/Requests";
import {
    StandardScreen,
    StandardTextField,
    StandardButton,
    StatusMessage,
    StandardTable,
    StandardTableHeader,
    StandardTableBody,
    StandardTableRow,
    StandardTableCell,
    PageLayout
} from '../components/Layout';

const MIN_BATCH_SIZE = 1;
const MAX_BATCH_SIZE = 100;
const HARDCODED_DEFAULT = 100;

// Overrides are stored under this prefix, but the endpoints take the bare URL.
const KEY_PREFIX = "FieldDetailsBatchSize__";

// Matches AppConfigurationValueManager's sentinel poll interval.
const PROPAGATION_NOTE = "Changes reach the provisioning engine within about a minute.";

const SOURCE_DESCRIPTIONS = {
    SiteValue: "A site-level override is configured for this exact site URL.",
    TenantValue: "No site override exists, so the tenant-level override applies.",
    ConfiguredDefault: "No site or tenant override exists, so the configured default applies.",
    HardcodedDefault: "Nothing is configured, so the built-in default of " + HARDCODED_DEFAULT + " applies."
};

class BatchSize extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            activeTab: 'overrides',
            IsAdmin: false,

            defaultValue: null,
            defaultLoaded: false,
            newDefaultValue: "",
            LoadingDefault: false,
            LoadingDefaultMessage: "",
            DefaultError: "",
            DefaultMessage: "",

            overrideSearchString: "",
            overrides: [],
            LoadingOverrides: false,
            LoadingOverridesMessage: "",
            OverridesError: "",
            OverridesMessage: "",
            OverridesWarning: "",

            tenantUrl: "",
            tenantValue: "",
            LoadingTenant: false,
            TenantError: "",
            TenantMessage: "",

            siteUrl: "",
            siteValue: "",
            LoadingSite: false,
            SiteError: "",
            SiteMessage: "",

            resolveUrl: "",
            resolveResult: null,
            LoadingResolve: false,
            ResolveError: "",

            LoadingRefresh: false,
            RefreshError: "",
            RefreshMessage: "",

            confirmHidden: true,
            confirmTitle: "",
            confirmSubText: "",
            confirmAction: null
        }
    }

    componentDidMount() {
        this.initializeSettings();
    }

    initializeSettings() {
        this.CheckIsAdmin();
        this.GetDefault();
    }

    // The API returns { error } on failure, but an auth rejection can arrive with no JSON body at all.
    async readErrorMessage(response) {
        try {
            let body = await response.json();
            if (body && body.error) {
                return "Something went wrong. " + body.error;
            }
        } catch (error) {
            // fall through to the generic message
        }
        return "Something went wrong.";
    }

    // A rejected request never produces a response, so the loading flag has to be cleared here or the
    // spinner runs forever with no way back.
    failWith(loadingKey, errorKey, error) {
        console.error(error);
        this.setState({
            [loadingKey]: false,
            [errorKey]: "Something went wrong. " + ((error && error.message) || "The request could not be completed.")
        });
    }

    validateBatchSize(value) {
        const trimmed = (value || "").trim();
        if (!/^\d+$/.test(trimmed)) {
            return "Batch size must be a whole number.";
        }
        const parsed = parseInt(trimmed, 10);
        if (parsed < MIN_BATCH_SIZE || parsed > MAX_BATCH_SIZE) {
            return "Batch size must be between " + MIN_BATCH_SIZE + " and " + MAX_BATCH_SIZE + ".";
        }
        return "";
    }

    requestConfirmation(title, subText, action) {
        this.setState({
            confirmHidden: false,
            confirmTitle: title,
            confirmSubText: subText,
            confirmAction: action
        });
    }

    dismissConfirmation() {
        this.setState({ confirmHidden: true, confirmAction: null });
    }

    runConfirmedAction() {
        const action = this.state.confirmAction;
        this.setState({ confirmHidden: true, confirmAction: null });
        if (action) {
            action();
        }
    }

    async CheckIsAdmin() {
        try {
            let response = await isAdmin(this.props.accessToken);
            if (response.status === 200) {
                let data = await response.json();
                this.setState({ IsAdmin: data.isAdmin });
            }
        } catch (error) {
            this.setState({ IsAdmin: false });
        }
    }

    async GetDefault() {
        this.setState({ LoadingDefault: true, LoadingDefaultMessage: "Fetching default batch size", DefaultError: "", DefaultMessage: "" });
        try {
            let response = await GetDefaultBatchSize(this.props.accessToken);
            if (response.status === 200) {
                let data = await response.json();
                this.setState({ LoadingDefault: false, defaultLoaded: true, defaultValue: data.value });
            } else {
                this.setState({ LoadingDefault: false, DefaultError: await this.readErrorMessage(response) });
            }
        } catch (error) {
            this.failWith("LoadingDefault", "DefaultError", error);
        }
    }

    async SetDefault() {
        const validationError = this.validateBatchSize(this.state.newDefaultValue);
        if (validationError) {
            this.setState({ DefaultError: validationError, DefaultMessage: "" });
            return;
        }

        this.setState({ LoadingDefault: true, LoadingDefaultMessage: "Updating default batch size", DefaultError: "", DefaultMessage: "" });
        try {
            let requestBody = { value: parseInt(this.state.newDefaultValue.trim(), 10) };
            let response = await SetDefaultBatchSize(this.props.accessToken, requestBody);
            if (response.status === 200) {
                this.setState({ newDefaultValue: "" });
                await this.GetDefault();
                this.setState({ DefaultMessage: "Default batch size updated. " + PROPAGATION_NOTE });
            } else if (response.status === 401 || response.status === 403) {
                this.setState({ LoadingDefault: false, DefaultError: "Only Admins can change the default batch size." });
            } else {
                this.setState({ LoadingDefault: false, DefaultError: await this.readErrorMessage(response) });
            }
        } catch (error) {
            this.failWith("LoadingDefault", "DefaultError", error);
        }
    }

    async DeleteDefault() {
        this.setState({ LoadingDefault: true, LoadingDefaultMessage: "Deleting default batch size", DefaultError: "", DefaultMessage: "" });
        try {
            let response = await DeleteDefaultBatchSize(this.props.accessToken);
            if (response.status === 200) {
                let data = await response.json();
                await this.GetDefault();
                this.setState({
                    DefaultMessage: data.deleted
                        ? "Default batch size deleted. " + PROPAGATION_NOTE
                        : "No default batch size was configured, so nothing was deleted."
                });
            } else if (response.status === 401 || response.status === 403) {
                this.setState({ LoadingDefault: false, DefaultError: "Only Admins can change the default batch size." });
            } else {
                this.setState({ LoadingDefault: false, DefaultError: await this.readErrorMessage(response) });
            }
        } catch (error) {
            this.failWith("LoadingDefault", "DefaultError", error);
        }
    }

    async GetOverrides() {
        this.setState({ LoadingOverrides: true, LoadingOverridesMessage: "Fetching overrides", OverridesError: "", OverridesMessage: "", OverridesWarning: "" });
        try {
            let requestBody = { searchString: this.state.overrideSearchString };
            let response = await GetBatchSizeOverrides(this.props.accessToken, requestBody);
            if (response.status === 200) {
                let overrides = await response.json();
                this.setState({
                    LoadingOverrides: false,
                    overrides: overrides,
                    OverridesError: overrides.length === 0 ? "No overrides matching the search string were found." : ""
                });
            } else {
                this.setState({ LoadingOverrides: false, overrides: [], OverridesError: await this.readErrorMessage(response) });
            }
        } catch (error) {
            this.setState({ overrides: [] });
            this.failWith("LoadingOverrides", "OverridesError", error);
        }
    }

    async SetScopedBatchSize(scope) {
        const isTenant = scope === "tenant";
        const scopeUrl = (isTenant ? this.state.tenantUrl : this.state.siteUrl).trim();
        const scopeValue = isTenant ? this.state.tenantValue : this.state.siteValue;
        const errorKey = isTenant ? "TenantError" : "SiteError";
        const messageKey = isTenant ? "TenantMessage" : "SiteMessage";
        const loadingKey = isTenant ? "LoadingTenant" : "LoadingSite";

        const validationError = this.validateBatchSize(scopeValue);
        if (validationError) {
            this.setState({ [errorKey]: validationError, [messageKey]: "" });
            return;
        }

        this.setState({ [loadingKey]: true, [errorKey]: "", [messageKey]: "" });
        try {
            let requestBody = { url: scopeUrl, value: parseInt(scopeValue.trim(), 10) };
            let response = isTenant
                ? await SetTenantBatchSize(this.props.accessToken, requestBody)
                : await SetSiteBatchSize(this.props.accessToken, requestBody);

            if (response.status === 200) {
                this.setState({
                    [loadingKey]: false,
                    [messageKey]: (isTenant ? "Tenant" : "Site") + " override set to " + requestBody.value + ". " + PROPAGATION_NOTE
                });
                await this.RefreshOverridesIfSearched();
            } else {
                this.setState({ [loadingKey]: false, [errorKey]: await this.readErrorMessage(response) });
            }
        } catch (error) {
            this.failWith(loadingKey, errorKey, error);
        }
    }

    urlFromKey(key) {
        const trimmed = key.trim();
        return trimmed.startsWith(KEY_PREFIX) ? trimmed.slice(KEY_PREFIX.length) : trimmed;
    }

    // A site URL keeps its path while a tenant host has none, so a key with a path can only have come
    // from a site override. A key without one is a tenant or root-site override, and those two share a
    // key by design, so either endpoint removes it.
    scopeForKey(key) {
        return this.urlFromKey(key).replace(/^https?:\/\//i, "").includes("/") ? "site" : "tenant";
    }

    async DeleteOverride(key) {
        this.setState({ LoadingOverrides: true, LoadingOverridesMessage: "Deleting override", OverridesError: "", OverridesMessage: "", OverridesWarning: "" });
        const requestBody = { url: this.urlFromKey(key) };
        const isSite = this.scopeForKey(key) === "site";
        const attempts = isSite
            ? [DeleteSiteBatchSize, DeleteTenantBatchSize]
            : [DeleteTenantBatchSize, DeleteSiteBatchSize];

        try {
            let deleted = false;
            for (const deleteBatchSize of attempts) {
                const response = await deleteBatchSize(this.props.accessToken, requestBody);
                if (response.status !== 200) {
                    this.setState({ LoadingOverrides: false, OverridesError: await this.readErrorMessage(response) });
                    return;
                }

                deleted = (await response.json()).deleted;
                if (deleted) {
                    break;
                }
            }

            await this.GetOverrides();
            this.setState({
                OverridesMessage: deleted ? "Override deleted. " + PROPAGATION_NOTE : "",
                OverridesWarning: deleted ? "" : "Nothing was deleted. No override is configured for '" + requestBody.url + "'."
            });
        } catch (error) {
            this.failWith("LoadingOverrides", "OverridesError", error);
        }
    }

    async RefreshOverridesIfSearched() {
        if (this.state.overrides.length > 0) {
            await this.GetOverrides();
        }
    }

    async ResolveEffective() {
        this.setState({ LoadingResolve: true, ResolveError: "", resolveResult: null });
        try {
            let requestBody = { url: this.state.resolveUrl.trim() };
            let response = await ResolveEffectiveBatchSize(this.props.accessToken, requestBody);
            if (response.status === 200) {
                let data = await response.json();
                this.setState({ LoadingResolve: false, resolveResult: data });
            } else {
                this.setState({ LoadingResolve: false, ResolveError: await this.readErrorMessage(response) });
            }
        } catch (error) {
            this.failWith("LoadingResolve", "ResolveError", error);
        }
    }

    async RefreshConfiguration() {
        this.setState({ LoadingRefresh: true, RefreshError: "", RefreshMessage: "" });
        try {
            let response = await RefreshBatchSizeConfiguration(this.props.accessToken);
            if (response.status === 200) {
                this.setState({ LoadingRefresh: false, RefreshMessage: "Sentinel key updated. " + PROPAGATION_NOTE });
            } else {
                this.setState({ LoadingRefresh: false, RefreshError: await this.readErrorMessage(response) });
            }
        } catch (error) {
            this.failWith("LoadingRefresh", "RefreshError", error);
        }
    }

    confirmDeleteOverride(key) {
        const url = this.urlFromKey(key);
        this.requestConfirmation(
            "Delete this override?",
            "'" + url + "' will fall back to the next level in the resolution chain. This action cannot be un-done.",
            () => this.DeleteOverride(key)
        );
    }

    renderOverridesTable() {
        const { overrides } = this.state;
        if (overrides.length === 0) {
            return null;
        }

        return (
            <StandardTable>
                <StandardTableHeader columns={['Tenant / Site URL', 'Batch Size', 'Delete']} />
                <StandardTableBody>
                    {overrides.map((override, index) => (
                        <StandardTableRow key={index} index={index}>
                            <StandardTableCell>{this.urlFromKey(override.key)}</StandardTableCell>
                            <StandardTableCell>{override.value}</StandardTableCell>
                            <StandardTableCell>
                                <PrimaryButton
                                    text="Delete"
                                    onClick={() => this.confirmDeleteOverride(override.key)}
                                    iconProps={{ iconName: 'Delete' }}
                                />
                            </StandardTableCell>
                        </StandardTableRow>
                    ))}
                </StandardTableBody>
            </StandardTable>
        );
    }

    renderDefaultValue() {
        const { LoadingDefault, LoadingDefaultMessage, defaultLoaded, defaultValue } = this.state;

        if (LoadingDefault) {
            return <Spinner size={SpinnerSize.medium} label={LoadingDefaultMessage} />;
        }
        if (!defaultLoaded) {
            return <span>Unknown</span>;
        }
        if (defaultValue === null || defaultValue === undefined) {
            return <span>Not set &mdash; the provisioning engine uses its built-in default of {HARDCODED_DEFAULT}.</span>;
        }
        return <span style={{ fontWeight: 'bold' }}>{defaultValue}</span>;
    }

    onOverrideSearchStringChange = (event, newValue) => {
        this.setState({ overrideSearchString: newValue, OverridesError: "", OverridesMessage: "", OverridesWarning: "", overrides: [] });
    };

    onNewDefaultValueChange = (event, newValue) => {
        this.setState({ newDefaultValue: newValue, DefaultError: "", DefaultMessage: "" });
    };

    onTenantUrlChange = (event, newValue) => {
        this.setState({ tenantUrl: newValue, TenantError: "", TenantMessage: "" });
    };

    onTenantValueChange = (event, newValue) => {
        this.setState({ tenantValue: newValue, TenantError: "", TenantMessage: "" });
    };

    onSiteUrlChange = (event, newValue) => {
        this.setState({ siteUrl: newValue, SiteError: "", SiteMessage: "" });
    };

    onSiteValueChange = (event, newValue) => {
        this.setState({ siteValue: newValue, SiteError: "", SiteMessage: "" });
    };

    onResolveUrlChange = (event, newValue) => {
        this.setState({ resolveUrl: newValue, ResolveError: "", resolveResult: null });
    };

    render() {
        const {
            activeTab,
            IsAdmin,
            newDefaultValue,
            LoadingDefault,
            DefaultError,
            DefaultMessage,
            overrideSearchString,
            overrides,
            LoadingOverrides,
            LoadingOverridesMessage,
            OverridesError,
            OverridesMessage,
            OverridesWarning,
            tenantUrl,
            tenantValue,
            LoadingTenant,
            TenantError,
            TenantMessage,
            siteUrl,
            siteValue,
            LoadingSite,
            SiteError,
            SiteMessage,
            resolveUrl,
            resolveResult,
            LoadingResolve,
            ResolveError,
            LoadingRefresh,
            RefreshError,
            RefreshMessage,
            confirmHidden,
            confirmTitle,
            confirmSubText
        } = this.state;

        return (
            <PageLayout>
                <Pivot
                    selectedKey={activeTab}
                    onLinkClick={(item) => this.setState({ activeTab: item.props.itemKey })}
                    styles={{
                        root: { marginBottom: '10px', minWidth: '600px', maxWidth: 'calc(100vw - 320px)' },
                        link: { fontSize: '16px' },
                        linkIsSelected: { fontSize: '16px' }
                    }}
                >
                    <PivotItem headerText="Overrides" itemKey="overrides">
                        <StandardScreen
                            title="Configured Overrides"
                            description="Search the configured tenant and site overrides. Leave the query empty to list them all. A tenant override shows as a bare host; a site override shows its full path."
                        >
                            <StandardTextField
                                label="Query"
                                placeholder="Text to search for"
                                value={overrideSearchString}
                                onChange={this.onOverrideSearchStringChange}
                            />

                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                                <StandardButton
                                    text='Search'
                                    onClick={() => this.GetOverrides()}
                                    disabled={LoadingOverrides}
                                    style={{ marginRight: '10px' }}
                                />
                                {LoadingOverrides && (
                                    <Spinner size={SpinnerSize.small} label={LoadingOverridesMessage} />
                                )}
                            </div>

                            {OverridesError && (
                                <StatusMessage type="error">{OverridesError}</StatusMessage>
                            )}
                            {OverridesWarning && (
                                <StatusMessage type="warning">{OverridesWarning}</StatusMessage>
                            )}
                            {OverridesMessage && (
                                <StatusMessage type="success">{OverridesMessage}</StatusMessage>
                            )}

                            {!LoadingOverrides && overrides.length > 0 && this.renderOverridesTable()}
                        </StandardScreen>

                        <StandardScreen
                            title="Tenant Override"
                            description="Set the batch size for every site in a tenant. Enter the tenant host, for example contoso.sharepoint.com. A scheme or trailing slash is tolerated and stripped. To remove an override, use the Configured Overrides list above."
                        >
                            <StandardTextField
                                label="Tenant Host"
                                placeholder="contoso.sharepoint.com"
                                value={tenantUrl}
                                onChange={this.onTenantUrlChange}
                            />

                            <StandardTextField
                                label="Batch Size"
                                placeholder={MIN_BATCH_SIZE + " to " + MAX_BATCH_SIZE}
                                value={tenantValue}
                                onChange={this.onTenantValueChange}
                                type="number"
                            />

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <StandardButton
                                    text='Set Override'
                                    onClick={() => this.SetScopedBatchSize("tenant")}
                                    disabled={LoadingTenant || !tenantUrl || !tenantValue}
                                />
                                {LoadingTenant && (
                                    <Spinner size={SpinnerSize.small} label="Updating tenant override..." />
                                )}
                            </div>

                            {TenantError && (
                                <StatusMessage type="error">{TenantError}</StatusMessage>
                            )}
                            {TenantMessage && (
                                <StatusMessage type="success">{TenantMessage}</StatusMessage>
                            )}
                        </StandardScreen>

                        <StandardScreen
                            title="Site Override"
                            description="Set the batch size for a single site. Enter the full absolute site URL, for example https://contoso.sharepoint.com/sites/test. A site override takes precedence over its tenant override. To remove an override, use the Configured Overrides list above."
                        >
                            <StandardTextField
                                label="Site URL"
                                placeholder="https://contoso.sharepoint.com/sites/test"
                                value={siteUrl}
                                onChange={this.onSiteUrlChange}
                            />

                            <StandardTextField
                                label="Batch Size"
                                placeholder={MIN_BATCH_SIZE + " to " + MAX_BATCH_SIZE}
                                value={siteValue}
                                onChange={this.onSiteValueChange}
                                type="number"
                            />

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <StandardButton
                                    text='Set Override'
                                    onClick={() => this.SetScopedBatchSize("site")}
                                    disabled={LoadingSite || !siteUrl || !siteValue}
                                />
                                {LoadingSite && (
                                    <Spinner size={SpinnerSize.small} label="Updating site override..." />
                                )}
                            </div>

                            {SiteError && (
                                <StatusMessage type="error">{SiteError}</StatusMessage>
                            )}
                            {SiteMessage && (
                                <StatusMessage type="success">{SiteMessage}</StatusMessage>
                            )}
                        </StandardScreen>
                    </PivotItem>

                    <PivotItem headerText="Default" itemKey="defaults">
                        <StandardScreen
                            title="Default (Global) Batch Size"
                            description={"The number of fields requested per CSOM batch for every site with no tenant or site override. Must be between " + MIN_BATCH_SIZE + " and " + MAX_BATCH_SIZE + "."}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '10px' }}>
                                <label style={{ fontSize: '1.1em' }}>
                                    Current: {this.renderDefaultValue()}
                                </label>
                                <StandardButton
                                    text='Refresh'
                                    onClick={() => this.GetDefault()}
                                    disabled={LoadingDefault}
                                />
                            </div>

                            <StandardTextField
                                label="New Default Batch Size"
                                placeholder={MIN_BATCH_SIZE + " to " + MAX_BATCH_SIZE}
                                value={newDefaultValue}
                                onChange={this.onNewDefaultValueChange}
                                type="number"
                                disabled={!IsAdmin}
                            />

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <StandardButton
                                    text='Set Default'
                                    onClick={() => this.SetDefault()}
                                    disabled={!IsAdmin || LoadingDefault || !newDefaultValue}
                                />
                                <StandardButton
                                    text='Delete Default'
                                    destructive
                                    onClick={() => this.requestConfirmation(
                                        "Delete the default batch size?",
                                        "Every site with no tenant or site override will fall back to the built-in default of " + HARDCODED_DEFAULT + ". This action cannot be un-done.",
                                        () => this.DeleteDefault()
                                    )}
                                    disabled={!IsAdmin || LoadingDefault}
                                />
                            </div>

                            {!IsAdmin && (
                                <StatusMessage type="info">Only Management Portal administrators can change the default batch size. Tenant and site overrides are on the Overrides tab.</StatusMessage>
                            )}
                            {DefaultError && (
                                <StatusMessage type="error">{DefaultError}</StatusMessage>
                            )}
                            {DefaultMessage && (
                                <StatusMessage type="success">{DefaultMessage}</StatusMessage>
                            )}
                        </StandardScreen>
                    </PivotItem>

                    <PivotItem headerText="Diagnostics" itemKey="diagnostics">
                        <StandardScreen
                            title="Resolve Effective Batch Size"
                            description="Work out which batch size a given site actually gets, and why. This mirrors the site, tenant, configured default and built-in default chain only. It does not account for the legacy FieldDetailsBatchSize50 and FieldDetailsBatchSize25 feature flags, so a customer still on those flags may behave differently to the value shown here."
                        >
                            <StandardTextField
                                label="Site URL"
                                placeholder="https://contoso.sharepoint.com/sites/test"
                                value={resolveUrl}
                                onChange={this.onResolveUrlChange}
                            />

                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                                <StandardButton
                                    text='Resolve'
                                    onClick={() => this.ResolveEffective()}
                                    disabled={LoadingResolve || !resolveUrl}
                                    style={{ marginRight: '10px' }}
                                />
                                {LoadingResolve && (
                                    <Spinner size={SpinnerSize.small} label="Resolving..." />
                                )}
                            </div>

                            {ResolveError && (
                                <StatusMessage type="error">{ResolveError}</StatusMessage>
                            )}

                            {resolveResult && (
                                <StatusMessage type="info">
                                    <div style={{ fontSize: '1.1em', fontWeight: 'bold' }}>
                                        Batch size {resolveResult.size} (source: {resolveResult.source})
                                    </div>
                                    <div>{SOURCE_DESCRIPTIONS[resolveResult.source] || "Unrecognised source."}</div>
                                </StatusMessage>
                            )}
                        </StandardScreen>

                        <StandardScreen
                            title="Refresh Configuration"
                            description="Bump the sentinel key so the provisioning engine re-reads its configuration. Every set and delete on this page already does this, so you only need it after editing Azure App Configuration directly, or to create the sentinel key as a one-time deployment prerequisite."
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <StandardButton
                                    text='Refresh'
                                    onClick={() => this.RefreshConfiguration()}
                                    disabled={LoadingRefresh}
                                    style={{ marginRight: '10px' }}
                                />
                                {LoadingRefresh && (
                                    <Spinner size={SpinnerSize.small} label="Refreshing..." />
                                )}
                            </div>

                            {RefreshError && (
                                <StatusMessage type="error">{RefreshError}</StatusMessage>
                            )}
                            {RefreshMessage && (
                                <StatusMessage type="success">{RefreshMessage}</StatusMessage>
                            )}
                        </StandardScreen>
                    </PivotItem>
                </Pivot>

                <Dialog
                    hidden={confirmHidden}
                    onDismiss={() => this.dismissConfirmation()}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: confirmTitle,
                        subText: confirmSubText
                    }}
                    modalProps={{
                        isBlocking: true,
                        styles: { main: { maxWidth: 450 } }
                    }}
                >
                    <DialogFooter>
                        <PrimaryButton onClick={() => this.runConfirmedAction()} text="Delete now" />
                        <DefaultButton onClick={() => this.dismissConfirmation()} text="Cancel" />
                    </DialogFooter>
                </Dialog>
            </PageLayout>
        );
    }
}

export default BatchSize;
